import { useToasts } from "react-toast-notifications";

import { usePrettyLogEventPage } from "@components/v2/analytics";
import { ClicksAndViewsEnhancedModellingBanner } from "@features/clicks-and-view-enhanced-modelling/clicks-and-views-enhanced-modelling";
import { useOverviewContext } from "@features/overview-v2/hooks";
import { AttributionWindowSelect } from "@pages/sales/report-body-control/attribution-window-select";
import { NbAccountingModeSelect } from "@pages/sales/report-body-control/nb-accounting-mode-select";
import { SecondaryButton } from "@shared/buttons";
import { AttributionModelSelect, GranularitySelect } from "@shared/selects";
import { generateAnonBase64Hash } from "@utils/anonymous";
import { usePrefs } from "@utils/client-side-preferences";

import { ConfigurationMenu } from "./configuration-menu";
import { SaveSavedViewsMenu } from "./save-saved-views-menu";
import { SavedViewsMenu } from "./saved-views";

export const TopBar = () => {
  const { isDemoMode } = usePrefs();
  const prettyLogEventPage = usePrettyLogEventPage();
  const { addToast } = useToasts();

  const {
    selectedSavedView,
    filters: {
      attributionModel,
      attributionWindow,
      accountingMode,
      level,
      dateRange,
      timeGranularity,
      dateRangeInterval,
      compareDateRange,
    },
    setAttributionModel,
    setAttributionWindow,
    setAccountingMode,
    setLevel,
  } = useOverviewContext();

  const pageContext = {
    dateRange,
    attributionModel,
    attributionWindow,
    accountingMode,
    timeGranularity,
    dateRangeInterval,
    compareDateRange,
    level,
  };

  const isAttributionWindowVisible = accountingMode === "accrual";

  return (
    <>
      <ClicksAndViewsEnhancedModellingBanner />
      <div className="px-6 py-4 flex justify-between space-x-4 bg-v2-primary border-b border-v2-tertiary text-v2-primary">
        <div className="flex items-end space-x-3 grow">
          <div className="flex gap-3 guide-saved-views">
            <div className="text-2xl font-bold max-w-xs min-w-[150px] truncate">
              {generateAnonBase64Hash({
                value: selectedSavedView?.name,
                prefix: "quick-filter",
                doAnon: isDemoMode && !selectedSavedView?.is_template,
              })}
            </div>
            <SavedViewsMenu />
          </div>
          <div className="border-l border-v2-secondary h-8" />
          <AttributionModelSelect
            maxWidth="160px"
            attributionModel={attributionModel}
            onChange={(value) => {
              prettyLogEventPage({
                domain: "Top Bar",
                action: "onChange",
                additionalActionInfo: "Set Attribution Model",
                data: {
                  newAttributionModel: value,
                  pageContext,
                },
              });

              setAttributionModel(value);
            }}
          />
          {isAttributionWindowVisible && (
            <AttributionWindowSelect
              maxWidth="160px"
              attributionModel={attributionModel}
              attributionWindow={attributionWindow}
              timeGranularity="daily"
              onChange={(value) => {
                prettyLogEventPage({
                  domain: "Top Bar",
                  action: "onChange",
                  additionalActionInfo: "Set Attribution Window",
                  data: {
                    newAttributionWindow: value,
                    pageContext,
                  },
                });

                setAttributionWindow(value);
              }}
            />
          )}
          <NbAccountingModeSelect
            maxWidth="190px"
            nbAccountingMode={accountingMode}
            isLoading={false}
            icon={null}
            onChange={(value) => {
              prettyLogEventPage({
                domain: "Top Bar",
                action: "onChange",
                additionalActionInfo: "Set Accounting Mode",
                data: {
                  newAccountingMode: value,
                  pageContext,
                },
              });

              setAccountingMode(value);
            }}
          />
          <GranularitySelect
            maxWidth="120px"
            hasPlatform={false}
            icon={null}
            label="LEVEL"
            granularity={level}
            onChange={(value) => {
              prettyLogEventPage({
                domain: "Top Bar",
                action: "onChange",
                additionalActionInfo: "Set Level",
                data: {
                  newLevel: value,
                  pageContext,
                },
              });

              setLevel(value);
            }}
          />
        </div>
        <div className="flex space-x-2 items-end">
          <SaveSavedViewsMenu />
          <SecondaryButton
            className="font-bold flex items-center gap-2 h-8 py-0"
            onClick={async () => {
              await navigator.clipboard.writeText(window.location.href);
              addToast("Copied to clipboard!", { appearance: "success" });
            }}
          >
            <i className="fa-regular fa-sharp fa-link text-sm" />
            SHARE
          </SecondaryButton>
          <ConfigurationMenu />
        </div>
      </div>
    </>
  );
};
